import { useHaptics } from '@/hooks/useHaptics';
import { BOUNCE, useSlidingIndicator } from '@/hooks/useSlidingIndicator';
import { cn } from '@/lib/cn';

/**
 * Controllo segmentato: N opzioni che stanno TUTTE nello schermo insieme.
 *
 * Quando le opzioni ci stanno, questa è la forma giusta — non chip separate.
 * Una fila di chip con bordo ciascuna mette selezionata e alternative sullo
 * stesso piano: si vede che c'è una scelta, non QUAL È. Qui il contenitore fa
 * da superficie comune e solo l'attiva si stacca come pastiglia piena, quindi
 * la selezione si legge senza confrontare i bordi fra loro.
 *
 * Le chip separate restano giuste dove le opzioni NON ci stanno e la fila
 * scorre in orizzontale (le News): lì un contenitore unico mentirebbe, perché
 * suggerirebbe che quello che vedi è tutto.
 *
 * La pastiglia attiva è UN SOLO elemento che scivola, non un fondo che si
 * accende e spegne su bottoni diversi: così l'occhio segue un oggetto che si
 * sposta e capisce da dove a dove è andata la selezione, invece di dover
 * ricostruire il cambiamento confrontando due fotogrammi.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  className,
}: {
  /** `count`: pastiglia numerica accanto all'etichetta (es. richieste in sospeso). */
  options: { value: T; label: string; disabled?: boolean; count?: number }[];
  value: T;
  onChange: (v: T) => void;
  className?: string;
}) {
  const haptics = useHaptics();
  const { containerRef, rect, ready } = useSlidingIndicator(value);

  return (
    <div
      ref={containerRef}
      role="tablist"
      className={cn(
        'card-edge relative flex shrink-0 gap-1 rounded-2xl bg-bg-secondary p-1',
        className,
      )}
    >
      {/* La pastiglia che scivola. `aria-hidden`: è decorazione, lo stato lo
          dice `aria-selected` sui bottoni. */}
      {rect && (
        <span
          aria-hidden
          className="absolute rounded-xl bg-brand"
          style={{
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
            transition: ready ? `all 320ms ${BOUNCE}` : undefined,
          }}
        />
      )}

      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={on}
            data-active={on}
            disabled={o.disabled}
            onClick={() => {
              if (on) return;
              haptics.selection();
              onChange(o.value);
            }}
            className={cn(
              // `relative` porta il testo SOPRA la pastiglia che scivola.
              'relative min-w-0 flex-1 truncate rounded-xl px-2 py-2 transition-colors',
              'text-xs font-bold active:opacity-80 disabled:opacity-30',
              // Nessun fondo sulle voci: il fondo è la pastiglia mobile. Resta
              // solo il colore del testo, che cambia in dissolvenza mentre la
              // pastiglia arriva.
              on ? 'text-brand-fg' : 'text-fg-muted',
            )}
          >
            {o.label}
            {o.count != null && o.count > 0 && (
              <span
                className={cn(
                  'ml-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-bold',
                  on ? 'bg-brand-fg/20 text-brand-fg' : 'bg-bg-tertiary text-fg',
                )}
              >
                {o.count > 99 ? '99+' : o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
